<template>
  <div v-if="location" class="mx-auto max-w-4xl space-y-12 pb-12">
    <header class="space-y-4">
      <NuxtLink
        :to="localePath('/locations')"
        class="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
      >
        <UIcon name="i-heroicons-arrow-left" class="h-4 w-4" />
        {{ t('locations.details.back_to_locations') }}
      </NuxtLink>
      <div>
        <h1 class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          {{ t(`locations.${location.slug}.name`) }}
        </h1>
        <p class="mt-2 text-lg font-medium text-primary-700">
          {{ t(`locations.${location.slug}.tagline`) }}
        </p>
      </div>
    </header>

    <LocationCarousel
      :images="location.images"
      :alt="t(`locations.${location.slug}.name`)"
    />

    <section class="space-y-3">
      <h2 class="text-xl font-bold text-gray-900">
        {{ t('locations.details.overview') }}
      </h2>
      <p class="leading-relaxed text-gray-700">
        {{ t(`locations.${location.slug}.overview`) }}
      </p>
    </section>

    <section v-if="pricingTiers.length > 0" class="space-y-3">
      <h2 class="text-xl font-bold text-gray-900">
        {{ t('locations.details.pricing') }}
      </h2>
      <ul class="grid gap-3 sm:grid-cols-2">
        <li
          v-for="(_, i) in pricingTiers"
          :key="i"
          class="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-4 text-gray-800 shadow-sm"
        >
          <UIcon
            name="i-heroicons-currency-yen"
            class="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500"
          />
          <span class="font-medium">
            {{ t(`locations.${location.slug}.pricing.tiers[${i}]`) }}
          </span>
        </li>
      </ul>
      <p
        v-if="hasPricingNote"
        class="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-relaxed text-amber-900"
      >
        <UIcon name="i-heroicons-information-circle" class="mr-1 inline h-4 w-4 align-text-bottom" />
        {{ t(`locations.${location.slug}.pricing.note`) }}
      </p>
    </section>

    <section class="space-y-3">
      <h2 class="text-xl font-bold text-gray-900">
        {{ t('locations.details.access') }}
      </h2>
      <p class="leading-relaxed text-gray-700">
        {{ t(`locations.${location.slug}.access`) }}
      </p>
    </section>

    <section v-if="highlightsItems.length > 0" class="space-y-3">
      <h2 class="text-xl font-bold text-gray-900">
        {{ t('locations.details.highlights') }}
      </h2>
      <ul class="space-y-2">
        <li
          v-for="(_, i) in highlightsItems"
          :key="i"
          class="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-4 text-gray-700 shadow-sm"
        >
          <UIcon
            name="i-heroicons-map-pin"
            class="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-500"
          />
          <span>{{ t(`locations.${location.slug}.highlights[${i}]`) }}</span>
        </li>
      </ul>
    </section>

    <section v-if="tipsItems.length > 0" class="space-y-3">
      <h2 class="text-xl font-bold text-gray-900">
        {{ t('locations.details.tips') }}
      </h2>
      <ul class="space-y-2">
        <li
          v-for="(_, i) in tipsItems"
          :key="i"
          class="flex items-start gap-3 rounded-xl border border-gray-100 bg-white p-4 text-gray-700 shadow-sm"
        >
          <UIcon
            name="i-heroicons-light-bulb"
            class="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500"
          />
          <span>{{ t(`locations.${location.slug}.tips[${i}]`) }}</span>
        </li>
      </ul>
    </section>

    <section v-if="location.contact" class="space-y-3">
      <h2 class="text-xl font-bold text-gray-900">
        {{ t('locations.details.contact') }}
      </h2>
      <div class="grid gap-3 sm:grid-cols-2">
        <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <div class="flex items-center gap-2 text-sm font-medium text-gray-500">
            <UIcon name="i-heroicons-phone" class="h-4 w-4" />
            {{ t('locations.details.phone_label') }}
          </div>
          <a
            :href="`tel:${location.contact.phone}`"
            class="mt-1 block text-base font-semibold text-primary-700 hover:text-primary-800"
          >
            {{ location.contact.phone }}
          </a>
        </div>
        <div class="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
          <div class="flex items-center gap-2 text-sm font-medium text-gray-500">
            <UIcon name="i-heroicons-user-circle" class="h-4 w-4" />
            {{ t('locations.details.cs_label') }}
          </div>
          <a
            :href="`tel:${location.contact.csPhone}`"
            class="mt-1 block text-base font-semibold text-primary-700 hover:text-primary-800"
          >
            {{ location.contact.csName }} · {{ location.contact.csPhone }}
          </a>
          <p class="mt-1 text-xs font-medium text-gray-500">
            {{ t(`locations.${location.slug}.cs_languages`) }}
          </p>
        </div>
      </div>
      <a
        :href="location.contact.homepageUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
      >
        {{ t('locations.details.homepage_label') }}
        <UIcon name="i-heroicons-arrow-top-right-on-square" class="h-4 w-4" />
      </a>
    </section>

    <section class="space-y-3">
      <div class="flex items-end justify-between gap-4">
        <h2 class="text-xl font-bold text-gray-900">
          {{ t('locations.details.find_us') }}
        </h2>
        <a
          :href="googleMapsUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
        >
          {{ t('locations.details.open_in_maps') }}
          <UIcon name="i-heroicons-arrow-top-right-on-square" class="h-4 w-4" />
        </a>
      </div>
      <LocationMapEmbed
        :src="location.mapEmbedSrc"
        :title="t(`locations.${location.slug}.name`)"
      />
    </section>
  </div>

  <div v-else class="mx-auto max-w-2xl py-20 text-center">
    <h1 class="text-2xl font-bold text-gray-900">
      {{ t('locations.details.not_found_title') }}
    </h1>
    <p class="mt-2 text-gray-600">
      {{ t('locations.details.not_found_subtitle') }}
    </p>
    <NuxtLink
      :to="localePath('/locations')"
      class="mt-6 inline-flex items-center gap-1 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-medium text-white shadow hover:bg-primary-700"
    >
      <UIcon name="i-heroicons-arrow-left" class="h-4 w-4" />
      {{ t('locations.details.back_to_locations') }}
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getLocationBySlug } from '~/data/locations'

const route = useRoute()
const { t, te, tm } = useI18n()
const localePath = useLocalePath()

const slug = computed(() => String(route.params.slug ?? ''))
const location = computed(() => getLocationBySlug(slug.value))

// `tm` returns the raw message tree so we can iterate arrays defined in the
// locale files. Falls back to an empty array when a section isn't provided
// for a particular location.
const arrayMessage = (key: string): unknown[] => {
  const items = tm(key)
  return Array.isArray(items) ? items : []
}

const pricingTiers = computed<unknown[]>(() =>
  location.value
    ? arrayMessage(`locations.${location.value.slug}.pricing.tiers`)
    : []
)

const hasPricingNote = computed(() =>
  location.value ? te(`locations.${location.value.slug}.pricing.note`) : false
)

const highlightsItems = computed<unknown[]>(() =>
  location.value
    ? arrayMessage(`locations.${location.value.slug}.highlights`)
    : []
)

const tipsItems = computed<unknown[]>(() =>
  location.value ? arrayMessage(`locations.${location.value.slug}.tips`) : []
)

const googleMapsUrl = computed(() => {
  if (!location.value) return '#'
  return `https://www.google.com/maps/search/?api=1&query=${location.value.lat},${location.value.lng}`
})

useHead(() => ({
  title: location.value
    ? `${t(`locations.${location.value.slug}.name`)} — bLink`
    : t('locations.details.not_found_title'),
  meta: location.value
    ? [
        {
          name: 'description',
          content: t(`locations.${location.value.slug}.overview`)
        }
      ]
    : []
}))
</script>
